import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOnboardingActions, useOnboardingState } from "@/hooks/queries/use-onboarding";

/**
 * Sends a user who still has a first-run step (name their organization, ask to
 * join one, or create one) to /welcome before the app.
 *
 * Never redirects while loading or on error: a slow or failed request must not
 * bounce an established user out of the page they asked for.
 */
export function OnboardingGuard({ children }: { children: ReactNode }) {
  const { data } = useOnboardingState();
  if (data && data.step !== "ready") {
    return <Navigate to="/welcome" replace />;
  }
  return <>{children}</>;
}

/** "Ada Lovelace (ada@acme.com) and Bob Smith (bob@acme.com)" */
export function adminList(admins: { name: string; email: string }[]): string {
  const names = admins.map((a) => `${a.name} (${a.email})`);
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1) ?? ""}`;
}

/**
 * Shown to a member who belongs to an organization but to no team yet (they
 * joined by request or by company domain): without a team they see no work,
 * and only an admin can add them. Names the admins and can email them.
 */
export function TeamAccessBanner() {
  const { data } = useOnboardingState();
  const { requestTeamAccess } = useOnboardingActions();
  if (data?.step !== "ready" || !data.needsTeamAccess) return null;
  const orgName = data.organizationName ?? "your organization";
  const error = requestTeamAccess.error instanceof Error ? requestTeamAccess.error.message : null;
  return (
    <div
      role="status"
      data-testid="team-access-banner"
      className="mb-4 flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/[0.07] px-4 py-2.5 text-sm text-foreground sm:flex-row sm:items-center"
    >
      <Info className="hidden h-4 w-4 shrink-0 text-primary sm:block" />
      <span className="flex-1">
        You&apos;re a member of <strong>{orgName}</strong>, but you aren&apos;t on any team yet, so
        there&apos;s no work to show.{" "}
        {data.admins.length > 0
          ? `Only an admin can add you to a team: ${adminList(data.admins)}.`
          : "Only an organization admin can add you to a team."}
        {error && <span className="block text-red-400">{error}</span>}
      </span>
      {data.admins.length > 0 &&
        (requestTeamAccess.isSuccess ? (
          <span className="shrink-0 text-muted-foreground">Request sent ✓</span>
        ) : (
          <Button
            size="sm"
            className="shrink-0"
            disabled={requestTeamAccess.isPending}
            onClick={() => {
              requestTeamAccess.mutate();
            }}
          >
            Ask for team access
          </Button>
        ))}
    </div>
  );
}
